import React, { useState, useEffect } from 'react';

const API_URL = '';

const styles = {
  page: { minHeight: '100vh', background: '#FDF8F5', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px 32px', background: '#FFFFFF', borderBottom: '1px solid #EDE8E3' },
  backBtn: { background: '#F5F0EB', border: 'none', borderRadius: '999px', padding: '10px 20px', fontSize: '14px', fontWeight: 500, color: '#6B6B6B', cursor: 'pointer' },
  title: { fontSize: '18px', fontWeight: 600, fontFamily: "'Playfair Display', serif" },
  insightsBtn: { marginLeft: 'auto', background: 'linear-gradient(135deg, #C3A8E1, #63C5DA)', border: 'none', borderRadius: '999px', padding: '12px 24px', fontWeight: 600, fontSize: '14px', color: '#FFFFFF', cursor: 'pointer' },
  content: { flex: 1, maxWidth: '760px', width: '100%', margin: '0 auto', padding: '32px 24px' },
  empty: { textAlign: 'center', padding: '80px 24px', color: '#A0A0A0' },
  emptyEmoji: { fontSize: '56px', marginBottom: '16px' },

  // Date folder
  folderWrapper: { marginBottom: '12px' },
  folderHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', background: '#FFFFFF', borderRadius: '16px', border: '1px solid #EDE8E3', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  folderIcon: { fontSize: '22px' },
  folderDate: { fontSize: '16px', fontWeight: 600, color: '#2D2D2D', fontFamily: "'Playfair Display', serif" },
  folderCount: { marginLeft: 'auto', fontSize: '12px', color: '#A0A0A0', background: '#F5F0EB', padding: '4px 10px', borderRadius: '999px' },
  folderChevron: (open) => ({ fontSize: '12px', color: '#A0A0A0', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginLeft: '4px' }),

  // Entry cards inside folder
  entriesContainer: { paddingLeft: '20px', marginTop: '8px' },
  entryCard: { background: '#FFFFFF', borderRadius: '16px', padding: '20px 24px', marginBottom: '10px', border: '1px solid #EDE8E3', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' },
  entryTime: { fontSize: '12px', color: '#A0A0A0', marginBottom: '10px' },
  entryTags: { display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' },
  tag: (color) => ({ padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 500, background: color }),
  entryContent: { fontSize: '15px', color: '#2D2D2D', lineHeight: 1.7, whiteSpace: 'pre-wrap' },

  // Insights panel
  insightsPanel: { background: 'linear-gradient(135deg, #F0EBF9, #E8F4F8)', borderRadius: '24px', padding: '32px', marginBottom: '32px', border: '1px solid rgba(195,168,225,0.3)' },
  insightsTitle: { fontSize: '20px', fontFamily: "'Playfair Display', serif", fontWeight: 600, marginBottom: '16px' },
  insightsText: { fontSize: '15px', color: '#2D2D2D', lineHeight: 1.8, whiteSpace: 'pre-wrap' },
  loading: { textAlign: 'center', padding: '32px', color: '#A0A0A0', fontSize: '15px' },
};

const tagColors = {
  'conversation': '#FFF3CD',
  'ai-session': '#EDE8F7',
  'voice-note': '#E8F7ED',
  'ai-insight': '#FDE8E8',
};

function groupByDate(entries) {
  const groups = {};
  entries.forEach(e => {
    const d = new Date(e.created_at);
    const key = d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  });
  return groups;
}

function getFolderEmoji(entries) {
  const tags = entries.flatMap(e => e.tags || []);
  if (tags.includes('ai-session') || tags.includes('conversation')) return '💬';
  if (tags.includes('voice-note')) return '🎙️';
  if (tags.includes('ai-insight')) return '💡';
  return '📖';
}

export default function HistoryPage({ navigate, userId }) {
  const [entries, setEntries] = useState([]);
  const [openFolders, setOpenFolders] = useState({});
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/diary/history?userId=${userId}`)
      .then(r => r.json())
      .then(data => { setEntries(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId]);

  const toggleFolder = (date) => setOpenFolders(prev => ({ ...prev, [date]: !prev[date] }));

  const generateInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await fetch(`${API_URL}/api/companion/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          message: 'Generate a deep life map analysis. Include: 1) CAUSE & EFFECT PATTERNS: What events trigger mood changes? Be specific. 2) HIDDEN STRENGTHS: What does this person do well that their history shows? 3) ENERGY DRAINS: What isn\'t bearing fruit? 4) HONEST REDIRECTS: Where might energy find better soil? 5) ONE FOCUS THIS WEEK. Be specific, reference actual entries, be honest not flattering.',
          mode: 'diary'
        }),
      });
      const data = await res.json();
      setInsights(data.response);
    } catch (e) { console.error(e); }
    setLoadingInsights(false);
  };

  const grouped = groupByDate(entries);
  const dates = Object.keys(grouped);
  const todayKey = new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('companion')}>← Back</button>
        <div style={styles.title}>My Life Map Diary</div>
        <button style={styles.insightsBtn} onClick={generateInsights}>
          {loadingInsights ? '⏳ Analyzing...' : '🧠 Generate Insights'}
        </button>
      </div>

      <div style={styles.content}>
        {insights && (
          <div style={styles.insightsPanel}>
            <div style={styles.insightsTitle}>🔍 Deep Life Map Analysis</div>
            <div style={styles.insightsText}>{insights}</div>
          </div>
        )}
        {loadingInsights && !insights && (
          <div style={styles.loading}>🧶 Reading your story and connecting the dots...</div>
        )}

        {loading && <div style={styles.loading}>Loading your diary...</div>}

        {!loading && entries.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyEmoji}>📖</div>
            <div style={{ fontSize: '18px', fontWeight: 500, color: '#2D2D2D' }}>Your story starts here</div>
            <div style={{ fontSize: '14px', marginTop: '8px', color: '#A0A0A0' }}>Save conversations or write journal entries to build your diary</div>
          </div>
        )}

        {dates.map(date => {
          const dayEntries = grouped[date];
          const isOpen = openFolders[date] !== undefined ? openFolders[date] : date === todayKey;
          return (
            <div key={date} style={styles.folderWrapper}>
              <div style={styles.folderHeader} onClick={() => toggleFolder(date)}>
                <span style={styles.folderIcon}>{getFolderEmoji(dayEntries)}</span>
                <span style={styles.folderDate}>{date}</span>
                <span style={styles.folderCount}>{dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}</span>
                <span style={styles.folderChevron(isOpen)}>▶</span>
              </div>

              {isOpen && (
                <div style={styles.entriesContainer}>
                  {dayEntries.map(entry => (
                    <div key={entry.id} style={styles.entryCard}>
                      <div style={styles.entryTime}>
                        {new Date(entry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {entry.tags && entry.tags.length > 0 && (
                        <div style={styles.entryTags}>
                          {entry.tags.map(tag => (
                            <span key={tag} style={styles.tag(tagColors[tag] || '#EDE8E3')}>{tag}</span>
                          ))}
                        </div>
                      )}
                      <div style={styles.entryContent}>{entry.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
